import React, { useEffect, useState } from 'react';
import { Menu, Bell, ArrowLeft, Eye, Save, Settings, Undo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PublishButtons, QuotationVersion } from '@/components/quotation/PublishButton';
import { quotationVersioningService } from '@/services/quotationVersioningService';
import { useToast } from '@/hooks/use-toast';

// Definição simplificada da interface Itinerary para evitar o erro de importação
interface Itinerary {
  id: string;
  title: string;
  client?: string;
  [key: string]: any;
}

interface HeaderProps {
  title?: string;
  isEditing?: boolean;
  onBack?: () => void;
  onPreview?: () => void;
  onSubmit?: (e?: React.FormEvent) => void | boolean | Promise<boolean | void>;
  showActions?: boolean;
  itinerary?: Itinerary;
  isSaving?: boolean;
  canUndo?: boolean;
  onUndo?: () => void;
  quotationId?: string; // ID da cotação para gerenciar versões
  onPublish?: () => void; // Callback para publicação
}

export function Header({ 
  title, 
  isEditing = false, 
  onBack, 
  onPreview, 
  onSubmit,
  showActions = false,
  itinerary,
  isSaving = false,
  canUndo = false,
  onUndo,
  quotationId,
  onPublish
}: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isItineraryRoute = location.pathname.includes('/admin/itineraries/');
  const isQuotationRoute = location.pathname.includes('/admin/quotations/');
  const isHotelSearchRoute = location.pathname.includes('/admin/hotel-search');
  
  // Estados para gerenciar as versões da cotação
  const [workVersion, setWorkVersion] = useState<QuotationVersion | undefined>(undefined);
  const [publishedVersion, setPublishedVersion] = useState<QuotationVersion | undefined>(undefined);
  const [historyVersion, setHistoryVersion] = useState<QuotationVersion | undefined>(undefined);
  
  // Carregar as versões quando o ID da cotação mudar
  useEffect(() => {
    if (isQuotationRoute && quotationId) {
      loadQuotationVersions();
    }
  }, [quotationId, isQuotationRoute]);
  
  // Função para carregar as versões da cotação
  const loadQuotationVersions = async () => {
    if (!quotationId) return;
    
    try {
      const versions = await quotationVersioningService.getQuotationVersions(quotationId);
      setWorkVersion(versions.workVersion || undefined);
      setPublishedVersion(versions.publishedVersion || undefined);
      setHistoryVersion(versions.historyVersion || undefined);
    } catch (error) {
      console.error('Error loading quotation versions:', error);
    }
  };
  
  // Função para publicar a cotação
  const handlePublish = async () => {
    if (!quotationId) return;
    
    try {
      toast({
        title: "Publicando cotação",
        description: "Aguarde enquanto a cotação é publicada...",
      });
      
      const published = await quotationVersioningService.publishQuotation(quotationId);
      
      if (published) {
        toast({
          title: "Cotação publicada",
          description: "A cotação foi publicada com sucesso.",
        });
        
        // Atualizar a lista de versões
        await loadQuotationVersions();
        
        // Chamar o callback de publicação, se fornecido
        if (onPublish) {
          onPublish();
        }
      } else {
        toast({
          title: "Erro ao publicar",
          description: "Ocorreu um erro ao publicar a cotação.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error publishing quotation:', error);
      toast({
        title: "Erro ao publicar",
        description: "Ocorreu um erro ao publicar a cotação.",
        variant: "destructive"
      });
    }
  };
  
  // Função para republicar uma versão histórica
  const handleRepublishVersion = async (version: QuotationVersion) => {
    try {
      toast({
        title: "Republicando versão",
        description: "Aguarde enquanto a versão é republicada...",
      });
      
      const republished = await quotationVersioningService.republishHistoryVersion(version.id);
      
      if (republished) {
        toast({
          title: "Versão republicada",
          description: "A versão histórica foi republicada com sucesso.",
        });
        
        // Atualizar a lista de versões
        await loadQuotationVersions();
        
        // Chamar o callback de publicação, se fornecido
        if (onPublish) {
          onPublish();
        }
      } else {
        toast({
          title: "Erro ao republicar",
          description: "Ocorreu um erro ao republicar a versão histórica.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error republishing version:', error);
      toast({
        title: "Erro ao republicar",
        description: "Ocorreu um erro ao republicar a versão histórica.",
        variant: "destructive"
      });
    }
  };
  
  // Função para abrir uma versão específica
  const handleOpenVersion = (version: QuotationVersion) => {
    if (version.version === 'work') {
      // Já estamos na versão de trabalho
      return;
    }
    
    // Abrir a versão publicada/histórica em uma nova aba
    const url = `/quotation/${version.id}`;
    window.open(url, '_blank');
  };
  
  // Handler para visualizar uma versão específica
  const handlePreviewVersion = (version: QuotationVersion) => {
    // Abrir a visualização da versão publicada/histórica em uma nova aba
    const url = `/quotation/${version.id}`;
    window.open(url, '_blank');
  };
  
  // Determinar o texto do botão de salvar com base na rota
  const getSaveButtonText = () => {
    if (isSaving) return "Salvando...";
    
    if (isQuotationRoute) {
      return "Salvar Cotação";
    }
    
    return isEditing ? 'Atualizar' : 'Publicar';
  };
  
  // Handler seguro que garante que o retorno é void para o onClick
  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onSubmit) {
      onSubmit(e);
    }
  };
  
  // Handler para salvar (usado pelos botões de cotação)
  const handleSave = () => {
    if (onSubmit) {
      onSubmit();
    }
  };
  
  return (
    <header className="bg-white border-b border-gray-200 shadow-sm w-full">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          {(isItineraryRoute || isQuotationRoute || isHotelSearchRoute) && onBack && (
            <Button 
              type="button" 
              variant="ghost" 
              size="icon"
              onClick={onBack}
              className="text-gray-500"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          
          {itinerary && (isEditing || location.pathname.includes('/admin/itineraries/edit/')) ? (
            <h1 className="text-xl font-semibold text-gray-800">
              {itinerary.title}
              {itinerary.client && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({itinerary.client})
                </span>
              )}
            </h1>
          ) : title && (
            <h1 className="text-xl font-semibold text-gray-800">
              {title}
            </h1>
          )}
        </div>
        
        {/* Botão de Desfazer no centro */}
        {(isItineraryRoute || isQuotationRoute) && onUndo && (
          <div className="flex-1 flex justify-center">
            <Button 
              onClick={onUndo} 
              disabled={!canUndo} 
              variant="outline" 
              size="sm"
              className="text-gray-500 hover:text-gray-700 hover:border-gray-300"
            >
              <Undo className="h-4 w-4 mr-2" />
              Desfazer
            </Button>
          </div>
        )}
        
        <div className="flex items-center space-x-3">
          {showActions && (
            <>
              {onPreview && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onPreview}
                  className="text-gray-600"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Visualizar
                </Button>
              )}
              
              {onSubmit && isQuotationRoute && quotationId ? (
                // Mostrar os botões de publicação para cotações
                <PublishButtons
                  onSave={handleSave}
                  onPublish={handlePublish}
                  onOpenVersion={handleOpenVersion}
                  onPreviewVersion={handlePreviewVersion}
                  onRepublishVersion={handleRepublishVersion}
                  workVersion={workVersion}
                  publishedVersion={publishedVersion}
                  historyVersion={historyVersion}
                  isSaving={isSaving}
                />
              ) : onSubmit && (
                // Botão padrão para outras páginas
                <Button 
                  type="submit" 
                  className="bg-voah-teal hover:bg-voah-teal/90 text-white"
                  form="itinerary-form"
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {!isSaving && <Save className="h-4 w-4 mr-2" />}
                  {getSaveButtonText()}
                </Button>
              )}
            </>
          )}
          
          <Button variant="ghost" size="icon" className="text-gray-500">
            <Bell className="h-5 w-5" />
          </Button>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/admin/settings">
                  <Button variant="ghost" size="icon" className="text-gray-500">
                    <Settings className="h-5 w-5" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Configurações</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-voah-teal text-white flex items-center justify-center font-medium">
              VA
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
